import React, { useState } from 'react';
import { Building2, FileText, Mail, Search, ToggleRight, Voicemail } from 'lucide-react';
import { useI18n } from '../../utils/i18n';
import apiService from '../../services/apiService';
import { invalidateFeaturesCache } from '../../utils/featuresCache';
import { useToast } from '../ToastProvider';

// #520: Reiter „Features“. Alle an- und abschaltbaren Funktionen an einer
// Stelle, nur die Schalter. Die Einstellungen der Funktionen bleiben in ihren
// Reitern und werden dort ausgegraut, wenn die Funktion aus ist.
//
// Muster aus #312: Den Hook ruft das Panel auf. Der Stand wird dort auch
// gebraucht (Reiter „Kunden“ ein-/ausblenden, KI-Rahmen ausgrauen), und
// `loadData()` füllt ihn über `setStand` zusammen mit den anderen Reitern.
// Jeder Schalter speichert sofort, wie vorher der Kunden-Schalter.
//
// #606: Die Darstellungs-Vorgabe (#565) ist kein Schalter und steht im Reiter
// „Servereinstellungen“. Sie speichert über denselben Hook (`handleSetFeature`),
// das Register `server/utils/featureSchalter.js` und die Zeile bleiben gleich.
export function useFeaturesTab({ setError }) {
  const { t } = useI18n();
  const { pushToast } = useToast();
  // null = noch nicht geladen oder Laden gescheitert; dann wird nichts ausgegraut
  const [features, setFeatures] = useState(null);
  const [aiBereit, setAiBereit] = useState(true);
  const [zeile, setZeile] = useState('');
  const [speichert, setSpeichert] = useState(null); // Name des Schalters, der gerade speichert

  const setStand = (antwort) => {
    setFeatures(antwort?.features || null);
    setAiBereit(antwort?.aiBereit !== false);
    setZeile(antwort?.zeile || '');
  };

  const handleSetFeature = async (name, wert) => {
    setError(null);
    setSpeichert(name);
    try {
      const antwort = await apiService.updateAdminFeatures({ [name]: wert });
      setStand(antwort);
      // Andere Ansichten lesen die Flags über den Client-Cache (#251)
      invalidateFeaturesCache();
      pushToast({ message: t('admin.features.saved', 'Gespeichert.'), type: 'success' });
    } catch (e) {
      console.error('Set feature error:', e);
      setError(e.response?.data?.error || t('admin.features.saveError', 'Der Schalter konnte nicht gespeichert werden.'));
    } finally {
      setSpeichert(null);
    }
  };

  return { features, aiBereit, zeile, speichert, setStand, handleSetFeature };
}

// Alle KI-Funktionen aus: Dann braucht auch die Anbieter-Verbindung niemand.
export function alleKiAus(features) {
  return !!features && features.searchAi === false && features.summaryAi === false
    && features.voicemailAi === false && features.mailAi === 'aus';
}

const FeaturesTab = ({ tab }) => {
  const { t } = useI18n();
  const { features, aiBereit, zeile, speichert, handleSetFeature } = tab;

  if (!features) {
    return (
      <div className="admin-section">
        <h3>{t('admin.features.title', 'Features')}</h3>
        <p className="section-description">{t('admin.features.loadError', 'Die Schalter konnten nicht geladen werden. Bitte „Aktualisieren“ versuchen.')}</p>
      </div>
    );
  }

  const ki = t('admin.features.inAiTab', 'Einstellungen im Reiter „AI Integration“');
  const eintraege = [
    { name: 'searchAi', icon: Search, titel: t('admin.features.searchAi', 'KI-Suche'), text: t('admin.features.searchAiDesc', 'KI-Antwort in der globalen Suche mit Folgefragen und „Ähnliche Tickets“ in der Detailansicht.'), ort: ki, ki: true },
    { name: 'summaryAi', icon: FileText, titel: t('admin.features.summaryAi', 'KI-Zusammenfassung'), text: t('admin.features.summaryAiDesc', 'Knöpfe „Zusammenfassen“ und „Beschreibung zusammenfassen“ am Ticket.'), ort: ki, ki: true },
    { name: 'mailAi', icon: Mail, titel: t('admin.features.mailAi', 'KI-Ticketerstellung aus Mails'), text: t('admin.ai.mailModeHint', 'Aufbereitung eingehender support@-Mails. „Schatten“ verändert nichts am Ticket — der Vorschlag landet als interner Kommentar zur Bewertung.'), ort: ki, ki: true, modus: [
      ['aus', t('admin.ai.mailModeOff', 'Aus — Mail-Text wie bisher übernehmen')],
      ['schatten', t('admin.ai.mailModeShadow', 'Schatten — Vorschlag als interner Kommentar')],
      ['scharf', t('admin.ai.mailModeLive', 'Scharf — KI-Beschreibung und Priorität setzen')],
    ] },
    { name: 'voicemailAi', icon: Voicemail, titel: t('admin.features.voicemailAi', 'KI-Voicemail-Tickets'), text: t('admin.ai.voicemailToggleHint', 'Placetel-AB-Nachrichten transkribieren und als aufbereitetes Ticket anlegen. Bei Inaktiv entsteht ein normales Mail-Ticket.'), ort: ki, ki: true },
    { name: 'customers', icon: Building2, titel: t('admin.features.customers', 'Kundenstamm'), text: t('admin.server.customersFeatureDesc', 'Blendet das Kundenfeld in Tickets, den Admin-Tab „Kunden" und die automatische Kundenzuordnung im Mail-Eingang komplett aus — für Installationen ohne Kundenbezug. Bestandsdaten bleiben erhalten.'), ort: t('admin.features.inCustomersTab', 'Verwaltung im Reiter „Kunden“') },
  ];

  return (
    <div className="admin-section">
      <h3><ToggleRight size={20} aria-hidden="true" /> {t('admin.features.title', 'Features')}</h3>
      <p className="section-description">{t('admin.features.description', 'Alle Funktionen, die sich an- und abschalten lassen. Jeder Schalter speichert sofort. Die Einstellungen bleiben in ihren Reitern und werden dort ausgegraut, solange die Funktion aus ist.')}</p>

      {!aiBereit && (
        <div className="feature-warnung" role="note">
          {t('admin.features.aiNotReady', 'Es ist noch kein KI-Anbieter eingerichtet (Reiter „AI Integration“: Base URL, API Key, Modell). Bis dahin bleiben die KI-Funktionen wirkungslos, auch wenn sie hier an sind.')}
        </div>
      )}

      <ul className="feature-liste">
        {eintraege.map(({ name, icon: Icon, titel, text, ort, modus }) => {
          const wert = features[name];
          const an = modus ? wert !== 'aus' : wert !== false;
          return (
            <li key={name} className={`feature-eintrag ${an ? '' : 'feature-eintrag--aus'}`}>
              <Icon size={20} aria-hidden="true" className="feature-icon" />
              <div className="feature-text">
                <span className="feature-titel" id={`feature-${name}`}>{titel}</span>
                <span className="feature-beschreibung">{text}</span>
                <span className="feature-ort">{ort}</span>
              </div>
              <div className="feature-schalter">
                {modus ? (
                  <select
                    aria-labelledby={`feature-${name}`}
                    value={wert}
                    disabled={speichert === name}
                    onChange={(e) => handleSetFeature(name, e.target.value)}
                  >
                    {modus.map(([w, text]) => <option key={w} value={w}>{text}</option>)}
                  </select>
                ) : (
                  <label className="status-toggle ai-schalter">
                    <input
                      type="checkbox"
                      aria-labelledby={`feature-${name}`}
                      checked={an}
                      disabled={speichert === name}
                      onChange={(e) => handleSetFeature(name, e.target.checked)}
                    />
                    <span className={`toggle-slider ${an ? 'enabled' : 'disabled'}`}></span>
                    <span className="status-text">{an ? t('common.active', 'Aktiv') : t('common.inactive', 'Inaktiv')}</span>
                  </label>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {zeile && (
        <div className="feature-zeile">
          <span>{t('admin.features.lineLabel', 'Stand als Zeile (für das Deploy-Protokoll):')}</span>
          <code>{zeile}</code>
        </div>
      )}
    </div>
  );
};

export default FeaturesTab;
